/**
 * /time-approval — единый раздел «Учёт времени» (задача 71 §5.1–§5.3, 82 §1.1,
 * 85 §5.3, 99 §2.2).
 *
 * Один conditional `hasConsole` (admin ИЛИ действует на юнит, task 97/98,
 * задача 99 §2.2): есть консоль → 3 таба ниже; нет → без полосы табов, только
 * `TimesheetEntryTab` своего времени (офис без прав на юнит — pmo/office_staff
 * без ведения юнита).
 *
 * Три таба: «Подтверждение» — линза консоли (`ApprovalLens`, задача 116 §2.2:
 * дневная `ApprovalConsole` / недельная `WeekFeedConsole` по `approval_mode`
 * юнитов); «Заполнить время» (85 §5.3) — внесение/правка за сотрудника или
 * себя на **любую** дату (`TimesheetEntryTab`, развязка со свободным выбором
 * сотрудника из консоли); «История» — сырая лента строк отчётов смены
 * (`TimesheetAuditTab`, перенесена с «Аудита», scope-aware: лид видит только
 * свою бригаду, админ — все). Active-таб в URL (`?tab=console|entry|history`);
 * выбор сотрудника/даты таба entry — отдельные ключи `?entry_user=`/`?entry_date=`.
 *
 * Тир 2 (экран + навигация + role-gate). Паттерны: `AppShell` рамка,
 * `PageHeader`/`PageTitle` шапка, `<GanttTabsBar>` (`patterns.md §Tab bar`),
 * `ApprovalConsole` + `TimesheetAuditTab` (`patterns.md §Рабочее место
 * руководителя`). Листание недель/выбранный день — search-params роута.
 */
import { useTranslation } from "react-i18next";

import { AppShell } from "@/layout/AppShell";
import { PageHeader, PageTitle } from "@/components/ui/page-header";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { GanttTabsBar } from "@/components/projects/GanttTabsBar";
import { ApprovalLens } from "@/components/approval/week-feed/ApprovalLens";
import { TimesheetEntryTab } from "@/components/approval/TimesheetEntryTab";
import { TimesheetAuditTab } from "@/components/audit/TimesheetAuditTab";

export type TimeApprovalTab = "console" | "entry" | "history";
export const TIME_APPROVAL_TABS: readonly TimeApprovalTab[] = ["console", "entry", "history"];

interface TimeApprovalPageProps {
  /**
   * Есть ли доступ к консоли подтверждения (admin ИЛИ действует на юнит из
   * орг-дерева, task 97/98). `true` → 3 таба (консоль/заполнить/история);
   * `false` → без полосы табов, только редактор своего времени (задача 99 §2.2).
   */
  hasConsole: boolean;
  tab: TimeApprovalTab;
  weekStart: string;
  selectedDate: string;
  selectedUnitId: number | null;
  includeChildren: boolean;
  openUserId?: number | null;
  /** Таб «Заполнить время»: выбранный сотрудник (`?entry_user=`). */
  entryUserId?: number | null;
  /** Таб «Заполнить время»: выбранная дата (`?entry_date=`). */
  entryDate?: string | null;
  onTabChange: (tab: TimeApprovalTab) => void;
  onWeekStartChange: (weekStart: string) => void;
  onSelectedDateChange: (date: string) => void;
  onUnitChange: (unitId: number | null) => void;
  onIncludeChildrenChange: (checked: boolean) => void;
  onEntryUserChange: (userId: number) => void;
  onEntryDateChange: (date: string) => void;
}

export function TimeApprovalPage({
  hasConsole,
  tab,
  weekStart,
  selectedDate,
  selectedUnitId,
  includeChildren,
  openUserId = null,
  entryUserId = null,
  entryDate = null,
  onTabChange,
  onWeekStartChange,
  onSelectedDateChange,
  onUnitChange,
  onIncludeChildrenChange,
  onEntryUserChange,
  onEntryDateChange,
}: TimeApprovalPageProps): React.ReactElement {
  const { t } = useTranslation();

  const handleTabChange = (next: string): void => {
    const cast = (TIME_APPROVAL_TABS as readonly string[]).includes(next)
      ? (next as TimeApprovalTab)
      : "console";
    onTabChange(cast);
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-4">
        <PageHeader>
          <div className="flex flex-col gap-0.5">
            <PageTitle>{t("timeApproval.title")}</PageTitle>
            <span className="text-sm text-[var(--fg-3)]">
              {t(hasConsole ? "timeApproval.subtitle" : "timeApproval.selfSubtitle")}
            </span>
          </div>
        </PageHeader>

        {hasConsole ? (
          <Tabs
            value={tab}
            onValueChange={handleTabChange}
            className="flex flex-col gap-4"
            data-testid="time-approval-tabs"
          >
            <GanttTabsBar
              tabs={TIME_APPROVAL_TABS}
              i18nNamespace="timeApproval.tabs"
              triggerTestIdPrefix="time-approval-tab"
            />

            <TabsContent value="console" className="mt-0">
              <ApprovalLens
                weekStart={weekStart}
                selectedDate={selectedDate}
                selectedUnitId={selectedUnitId}
                includeChildren={includeChildren}
                openUserId={openUserId}
                onWeekStartChange={onWeekStartChange}
                onSelectedDateChange={onSelectedDateChange}
                onUnitChange={onUnitChange}
                onIncludeChildrenChange={onIncludeChildrenChange}
              />
            </TabsContent>

            <TabsContent value="entry" className="mt-0">
              <TimesheetEntryTab
                selectedUserId={entryUserId}
                selectedDate={entryDate}
                onUserChange={onEntryUserChange}
                onDateChange={onEntryDateChange}
              />
            </TabsContent>

            <TabsContent value="history" className="mt-0">
              <TimesheetAuditTab />
            </TabsContent>
          </Tabs>
        ) : (
          // Нет консоли (офис без прав на юнит): без полосы табов — только
          // редактор своего времени (defaultToSelf, без выбора подчинённых).
          <TimesheetEntryTab
            selectedUserId={entryUserId}
            selectedDate={entryDate}
            onUserChange={onEntryUserChange}
            onDateChange={onEntryDateChange}
            defaultToSelf
            showSubordinatePicker={false}
          />
        )}
      </div>
    </AppShell>
  );
}
